import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import axios from '../../../api/axios';
import { ApiError } from '../../../types/errors';
import { validatePassword } from '../../../utils/Utils';
import { PASSWORD_VALIDATION } from '../../../utils/Constants';

interface ChangePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

interface FormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface FormErrors {
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
  submit?: string;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ show, onHide }) => {
  const [formData, setFormData] = useState<FormData>({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    if (show) {
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setErrors({});
      setSuccess('');
    }
  }, [show]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.current_password) {
      newErrors.current_password = '現在のパスワードを入力してください。';
    }

    if (!formData.new_password) {
      newErrors.new_password = '新しいパスワードを入力してください。';
    } else if (!validatePassword(formData.new_password)) {
      newErrors.new_password = PASSWORD_VALIDATION.ERROR_MESSAGE;
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = '確認用パスワードを入力してください。';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'パスワードが一致しません';
    }

    return newErrors;
  };

  const handleSubmit = async (): Promise<void> => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/change-password', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setErrors({});

      onHide();

      alert('パスワードが正常に変更されました');
    } catch (error: unknown) {
      console.error('Error changing password:', error);
      const axiosError = error as ApiError;
      if (axiosError.response && axiosError.response.status === 401) {
        setErrors({ current_password: '現在のパスワードが正しくありません' });
      } else if (axiosError.response && axiosError.response.status === 422) {
        setErrors({ new_password: PASSWORD_VALIDATION.ERROR_MESSAGE });
      } else {
        setErrors({ submit: 'パスワード変更中にエラーが発生しました。もう一度お試しください。' });
        alert('パスワード変更中にエラーが発生しました。もう一度お試しください。');
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='sm'
      centered
      backdrop="static"  // Prevent close when clicking outside
      keyboard={false}   // Optional: Prevent closing with ESC key
    >
      <Modal.Header closeButton>
        <Modal.Title>パスワード変更</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='candidate-edit-panel'>
          <Form>
          <div className="candidate-detail-item">
            <div className="candidate-detail-label">現在のパスワード <span className="required-mark">*</span></div>
            <div className="candidate-detail-value">
                <Form.Control
                  type="password"
                  name="current_password"
                  value={formData.current_password}
                  onChange={handleChange}
                  isInvalid={!!errors.current_password}
                  placeholder="現在のパスワード"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.current_password}
                </Form.Control.Feedback>
            </div>
          </div>
          <div className="candidate-detail-item">
            <div className="candidate-detail-label">新しいパスワード <span className="required-mark">*</span></div>
            <div className="candidate-detail-value">
              <Form.Control
                type="password"
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                isInvalid={!!errors.new_password}
                placeholder="新しいパスワード"
              />
              <Form.Control.Feedback type="invalid">
                {errors.new_password}
              </Form.Control.Feedback>
              <div className="d-flex justify-content-between mt-2">
                <small className="text-muted">{PASSWORD_VALIDATION.SHORT_COMMENT}</small>
              </div>
            </div>
          </div>
          <div className="candidate-detail-item">
            <div className="candidate-detail-label">パスワード（確認用）<span className="required-mark">*</span></div>
            <div className="candidate-detail-value">
              <Form.Control
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                isInvalid={!!errors.confirm_password}
                placeholder="新しいパスワード（確認用）"
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirm_password}
              </Form.Control.Feedback>
            </div>
          </div>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={handleSubmit}>
          変更
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePasswordModal;
